/**
 * TokenRefreshManager — singleton that handles transparent 401 → refresh → retry.
 *
 * Both HttpService and HttpCoreService delegate 401 errors here.
 * Uses a raw axios call (not httpService) to avoid circular dependencies
 * and interceptor loops.
 *
 * Queue pattern: while a refresh is in-flight, subsequent 401s are queued
 * and replayed once the new access token arrives.
 */
import axios, { type InternalAxiosRequestConfig } from "axios";
import { currentConfig } from "../config/environment";
import {
  getRefreshToken,
  setToken,
  setRefreshToken,
  setStoredUser,
  clearToken,
} from "../utils/auth";

/** Axios config extended with a retry flag to prevent infinite loops. */
interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface QueueItem {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}

class TokenRefreshManager {
  private isRefreshing = false;
  private failedQueue: QueueItem[] = [];

  // -----------------------------------------------------------------------
  // Public API — called from HTTP service response interceptors
  // -----------------------------------------------------------------------

  /**
   * Attempt to refresh the access token and replay the failed request.
   *
   * @param error The original Axios error (must be a 401).
   * @returns A Promise that resolves with the replayed response.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async handleUnauthorized(error: any): Promise<any> {
    const originalRequest: RetryableRequest = error.config;

    // Guard: if this request was already retried, don't loop
    if (originalRequest._retry) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    // If a refresh is already in-flight, queue this request
    if (this.isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      }).then((newToken) => {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      });
    }

    // Start the refresh
    this.isRefreshing = true;
    const refreshTokenValue = getRefreshToken();

    if (!refreshTokenValue) {
      this.isRefreshing = false;
      this.processQueue(new Error("No refresh token available"), null);
      this.forceLogout();
      return Promise.reject(error);
    }

    try {
      // Direct axios call — bypasses interceptors
      const response = await axios.post(
        `${currentConfig.apiBaseUrl}/auth/refresh`,
        { token: refreshTokenValue },
      );

      // Backend wraps in { statusCode, message, data: { accessToken, ... } }
      const data = response.data?.data ?? response.data;
      const { accessToken, refreshToken: newRefreshToken, user } = data;

      if (!accessToken) {
        throw new Error("Refresh response missing accessToken");
      }

      // Persist new tokens
      setToken(accessToken);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }
      if (user) {
        setStoredUser(user);
      }

      // Unblock all queued requests with the new token
      this.processQueue(null, accessToken);

      // Replay the original request
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      this.processQueue(refreshError, null);
      this.forceLogout();
      return Promise.reject(refreshError);
    } finally {
      this.isRefreshing = false;
    }
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  private processQueue(error: unknown, token: string | null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (token) {
        resolve(token);
      } else {
        reject(error);
      }
    });
    this.failedQueue = [];
  }

  private forceLogout(): void {
    clearToken();
    // Avoid redirect loops — do not redirect from public pages that make
    // unauthenticated API calls (login, register). Those pages handle their
    // own error states.
    const publicPaths = [
      "/login",
      "/register",
      "/forgot-password",
      "/reset-password",
      "/cda/sign", // Public PI signing page — unauthenticated API calls must not trigger redirect
    ];
    const isPublicPage =
      typeof window !== "undefined" &&
      publicPaths.some((p) => window.location.pathname.startsWith(p));
    if (typeof window !== "undefined" && !isPublicPage) {
      window.location.href = "/login";
    }
  }
}

export const tokenRefreshManager = new TokenRefreshManager();
